/**
 * 0.44.70 - Date patterns for rules: =formatdate(value, "dd.MM.yyyy") and
 * =parsedate(text, "dd.MM.yyyy").
 *
 * The letters are the ones API connections use (backend
 * services/api_dates.py): yyyy yy MMMM MMM MM M dd d HH H hh h mm ss SSS
 * a, plus T and Z as themselves; any other character that is not a
 * letter is itself. Rules work in ISO (2026-10-03, 2026-10-03T14:30); these
 * two are for the cases an API connection cannot foresee, such as a date
 * inside a longer text.
 *
 * formatdate writes month names (MMM / MMMM) in the page's language, from
 * the built-in library Months and days, because its result is for
 * people: after a day number, the Month in a date form (0.44.71). parsedate reads English names and the page language's.
 */
import { monthNameFor, monthNamesNow, formatCalendarDate, formatCalendarDateTime } from '../utils/format';

const TOKENS = ['yyyy', 'yy', 'MMMM', 'MMM', 'MM', 'M', 'dd', 'd', 'HH', 'H',
  'hh', 'h', 'mm', 'ss', 'SSS', 'a'];
const EN = ['january', 'february', 'march', 'april', 'may', 'june', 'july',
  'august', 'september', 'october', 'november', 'december'];

export function tokenize(fmt) {
  const out = [];
  const s = String(fmt || '');
  let i = 0;
  while (i < s.length) {
    const tok = TOKENS.find(t => s.startsWith(t, i));
    if (tok) { out.push(['tok', tok]); i += tok.length; continue; }
    const ch = s[i];
    if (/[A-Za-z]/.test(ch) && ch !== 'T' && ch !== 'Z') return null;
    out.push(['lit', ch]);
    i += 1;
  }
  return out;
}

const pad = (n, w = 2) => String(n).padStart(w, '0');

/* ISO (or yyyy-MM-dd HH:mm) to {y, m, d, h, mi, s, time}; null if not. */
export function readIso(value) {
  const m = String(value ?? '').trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return null;
  const v = { y: +m[1], m: +m[2], d: +m[3], h: +(m[4] || 0), mi: +(m[5] || 0), s: +(m[6] || 0), time: !!m[4] };
  return valid(v) ? v : null;
}

function valid(v) {
  if (!(v.m >= 1 && v.m <= 12 && v.d >= 1 && v.h <= 23 && v.mi <= 59 && v.s <= 59)) return false;
  const last = new Date(Date.UTC(v.y, v.m, 0)).getUTCDate();
  return v.d <= last;
}

export function formatDatePattern(value, fmt) {
  if (value === undefined || value === null || value === '') return '';
  const v = readIso(value);
  if (!v) return '';
  const f = String(fmt ?? '').trim();
  if (!f) return v.time ? formatCalendarDateTime(String(value)) : formatCalendarDate(String(value));
  const parts = tokenize(f);
  if (!parts) return '';
  const h12 = (v.h % 12) || 12;
  // 0.44.71 - With a day in the pattern, the month as written in a date
  // (11 հունվարի 2026, 25 июня 2024); without one, the plain name.
  const inDate = parts.some(([k, t]) => k === 'tok' && (t === 'dd' || t === 'd'));
  return parts.map(([k, t]) => (k === 'lit' ? t : ({
    yyyy: pad(v.y, 4), yy: pad(v.y % 100), MMMM: monthNameFor(v.m, false, inDate), MMM: monthNameFor(v.m, true, inDate),
    MM: pad(v.m), M: String(v.m), dd: pad(v.d), d: String(v.d), HH: pad(v.h), H: String(v.h),
    hh: pad(h12), h: String(h12), mm: pad(v.mi), ss: pad(v.s), SSS: '000', a: v.h >= 12 ? 'PM' : 'AM',
  })[t])).join('');
}

function monthIndex(word) {
  const w = String(word || '').toLowerCase().replace(/\.$/, '');
  if (!w) return 0;
  let i = EN.findIndex(n => n === w || n.slice(0, 3) === w);
  if (i >= 0) return i + 1;
  const names = monthNamesNow();
  for (const list of [names.long, names.dateLong, names.short, names.dateShort]) {
    i = list.findIndex(n => n.toLowerCase().replace(/\.$/, '') === w);
    if (i >= 0) return i + 1;
  }
  return 0;
}

const esc = (c) => c.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

export function parseDatePattern(text, fmt) {
  if (text === undefined || text === null || String(text).trim() === '') return '';
  const f = String(fmt ?? '').trim();
  if (!f) { const v = readIso(text); return v ? isoOf(v) : ''; }
  const parts = tokenize(f);
  if (!parts) return '';
  let rx = '';
  const names = [];
  for (const [k, t] of parts) {
    if (k === 'lit') { rx += t === ' ' ? '\\s+' : esc(t); continue; }
    names.push(t);
    rx += ({
      yyyy: '(\\d{4})', yy: '(\\d{2})', MMMM: '([^\\d\\s.,/-]+)', MMM: '([^\\d\\s.,/-]+)\\.?',
      MM: '(\\d{2})', M: '(\\d{1,2})', dd: '(\\d{2})', d: '(\\d{1,2})', HH: '(\\d{2})', H: '(\\d{1,2})',
      hh: '(\\d{2})', h: '(\\d{1,2})', mm: '(\\d{2})', ss: '(\\d{2})', SSS: '(\\d{1,9})', a: '([AaPp]\\.?[Mm]\\.?)',
    })[t];
  }
  const m = String(text).trim().match(new RegExp(`^${rx}$`, 'i'));
  if (!m) return '';
  const v = { y: null, m: null, d: 1, h: 0, mi: 0, s: 0, time: false };
  let pm = null;
  names.forEach((t, i) => {
    const g = m[i + 1];
    if (t === 'yyyy') v.y = +g;
    else if (t === 'yy') v.y = +g < 70 ? 2000 + +g : 1900 + +g;
    else if (t === 'MMMM' || t === 'MMM') v.m = monthIndex(g) || null;
    else if (t === 'MM' || t === 'M') v.m = +g;
    else if (t === 'dd' || t === 'd') v.d = +g;
    else if (/^[Hh]/.test(t)) { v.h = +g; v.time = true; }
    else if (t === 'mm') { v.mi = +g; v.time = true; }
    else if (t === 'ss') v.s = +g;
    else if (t === 'a') pm = /^p/i.test(g);
  });
  if (v.y === null || !v.m) return '';
  if (pm !== null) {
    if (v.h < 1 || v.h > 12) return '';
    v.h = (v.h % 12) + (pm ? 12 : 0);
  }
  return valid(v) ? isoOf(v) : '';
}

function isoOf(v) {
  const base = `${pad(v.y, 4)}-${pad(v.m)}-${pad(v.d)}`;
  if (!v.time) return base;
  return `${base}T${pad(v.h)}:${pad(v.mi)}${v.s ? `:${pad(v.s)}` : ''}`;
}
